import React, { useEffect, useState } from "react";
import "./UndoToast.css";

/** How long an undo toast stays before it dismisses itself. */
export const UNDO_TOAST_DURATION_MS = 6000;

const isEditableTarget = (target) => {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])"));
};

/**
 * Small bottom-center toast with an Undo action (button or Cmd/Ctrl+Z).
 * Auto-dismisses after `durationMs`; hovering or focusing the toast pauses that, and leaving
 * restarts the full countdown. A new `toast.id` replaces the previous toast and re-arms the timer.
 * Cmd/Ctrl+Z is ignored while typing in a field so native text undo keeps working.
 *
 * @example
 * <UndoToast toast={{ id: 1, message: "Marked done" }} onUndo={undo} onDismiss={clear} />
 */
const UndoToast = ({ toast, onUndo, onDismiss, durationMs = UNDO_TOAST_DURATION_MS }) => {
  const toastId = toast?.id;
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setPaused(false);
  }, [toastId]);

  useEffect(() => {
    if (toastId == null || paused) return undefined;
    const timerId = window.setTimeout(onDismiss, durationMs);
    return () => window.clearTimeout(timerId);
  }, [toastId, paused, durationMs, onDismiss]);

  useEffect(() => {
    if (toastId == null) return undefined;
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || event.shiftKey || event.altKey) return;
      if (!(event.metaKey || event.ctrlKey) || String(event.key).toLowerCase() !== "z") return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      onUndo();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toastId, onUndo]);

  if (!toast) return null;

  return (
    <div className="undo-toast-region">
      <div
        className="undo-toast"
        role="status"
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <span className="undo-toast__message">{toast.message}</span>
        <button type="button" className="undo-toast__action" onClick={onUndo}>
          Undo
        </button>
      </div>
    </div>
  );
};

export default UndoToast;
